import simpleheat from "simpleheat";

export interface HeatmapOverlayStyle {
  /** Point radius in screen pixels. */
  radius: number;
  /** Blur radius in screen pixels. */
  blur: number;
  /** Accumulated value that maps to full intensity (lower = saturates sooner). */
  max: number;
  /** Whole-layer canvas opacity (0–1). */
  opacity: number;
  /** Intensity stop (0–1) → CSS color. First stop should be low so faint areas
   * are colored rather than translucent black. */
  gradient: Record<number, string>;
}

type HeatPoint = { lat: number; lng: number };

/** A Google Maps overlay that renders points as a heatmap. */
export interface HeatmapOverlay extends google.maps.OverlayView {
  /** Replace the rendered points and redraw. */
  setPoints(points: HeatPoint[]): void;
}

/**
 * Build a canvas heatmap overlay for Google Maps using `simpleheat`.
 *
 * Google removed the built-in `visualization.HeatmapLayer` in Maps JS v3.65, so
 * we draw our own: a `<canvas>` in the overlay pane, with each lat/lng projected
 * to screen pixels on every `draw()` (which Google fires on pan/zoom/resize).
 *
 * MUST be called after the Maps JS API has loaded — it subclasses
 * `google.maps.OverlayView`, which doesn't exist until then.
 */
export const createHeatmapOverlay = (
  maps: typeof google.maps,
  initialPoints: HeatPoint[],
  style: HeatmapOverlayStyle,
): HeatmapOverlay => {
  class HeatmapOverlayView extends maps.OverlayView implements HeatmapOverlay {
    private canvas: HTMLCanvasElement | null = null;
    private heat: simpleheat.Instance | null = null;
    private points: HeatPoint[] = initialPoints;

    setPoints(points: HeatPoint[]) {
      this.points = points;
      this.draw();
    }

    override onAdd() {
      const canvas = document.createElement("canvas");
      canvas.style.position = "absolute";
      canvas.style.pointerEvents = "none";
      canvas.style.opacity = String(style.opacity);
      this.canvas = canvas;
      this.heat = simpleheat(canvas);
      this.heat.radius(style.radius, style.blur);
      this.heat.gradient(style.gradient);
      this.heat.max(style.max);
      // overlayLayer is non-interactive — keeps map drag/zoom working under it.
      this.getPanes()?.overlayLayer.appendChild(canvas);
    }

    override draw() {
      const projection = this.getProjection();
      const canvas = this.canvas;
      const heat = this.heat;
      if (!projection || !canvas || !heat) return;

      const map = this.getMap() as google.maps.Map | undefined;
      const bounds = map?.getBounds();
      const div = map?.getDiv() as HTMLElement | undefined;
      if (!bounds || !div) return;

      const width = div.offsetWidth;
      const height = div.offsetHeight;

      // Pin the canvas to the viewport's top-left (NW corner) in div-pixel space,
      // so projected point pixels line up after we subtract that origin.
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const origin = projection.fromLatLngToDivPixel(new maps.LatLng(ne.lat(), sw.lng()));
      if (!origin) return;

      canvas.width = width;
      canvas.height = height;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.style.left = `${origin.x}px`;
      canvas.style.top = `${origin.y}px`;
      // simpleheat caches the canvas size from construction time; without this
      // it only colorizes the stale top-left region and the rest stays black.
      heat.resize();

      const data: Array<[number, number, number]> = [];
      for (const p of this.points) {
        const px = projection.fromLatLngToDivPixel(new maps.LatLng(p.lat, p.lng));
        if (!px) continue;
        const x = px.x - origin.x;
        const y = px.y - origin.y;
        if (x < 0 || y < 0 || x > width || y > height) continue; // cull offscreen
        data.push([x, y, 1]);
      }

      heat.data(data);
      heat.draw();
    }

    override onRemove() {
      this.canvas?.parentNode?.removeChild(this.canvas);
      this.canvas = null;
      this.heat = null;
    }
  }

  return new HeatmapOverlayView();
};
